import { Link } from 'react-router-dom';
import type { OperationalAttentionItem, OrganizationHealth, ProjectHealthSummary } from '../../api';
import { ResponsiveTable } from '../primitives/ResponsiveTable';
import type { Column } from '../primitives/ResponsiveTable';
import { LoadingRows } from '../ui';
import { AttentionFeed } from '../status/AttentionFeed';
import type { FeedItem } from '../status/AttentionFeed';
import { InfrastructureSummary, OutcomeBadge, SiteBadge, Timestamp, attemptNote } from '../status/StatusPieces';
import { compareSite, groupByCategory, infrastructureOf, latestAttemptOf, siteOf } from '../status/statusModel';
import { itemEnvironmentName, naturalCompare, portfolioItemPath } from '../project/operationalHealth';
import { plural } from '../../labels';
import { relativeTime } from '../project/projectHealth';

/**
 * The Dashboard's view of every store (CO-150). It leads with what visitors
 * get — each project's live site — and keeps the other two questions in
 * their own columns: how the latest deployment went, and whether the
 * infrastructure warns. A failed update next to an operational site reads
 * as exactly that, not as a red project.
 */

const siteRank = (summary: ProjectHealthSummary) => siteOf(summary.primary).state;

/** Worst site first, then the most attention, then by name. */
export function bySiteUrgency(a: ProjectHealthSummary, b: ProjectHealthSummary): number {
  const site = compareSite(siteRank(a), siteRank(b));
  if (site !== 0) return site;
  if (a.critical !== b.critical) return b.critical - a.critical;
  if (a.attention.length !== b.attention.length) return b.attention.length - a.attention.length;
  return naturalCompare(a.name, b.name);
}

/** "1 unavailable · 2 degraded · 4 operational". */
export function siteCounts(projects: ProjectHealthSummary[]): string {
  const counts = { unavailable: 0, degraded: 0, operational: 0, unknown: 0 };
  for (const project of projects) counts[siteRank(project)] += 1;
  return [
    counts.unavailable ? `${counts.unavailable} unavailable` : null,
    counts.degraded ? `${counts.degraded} degraded` : null,
    counts.operational ? `${counts.operational} operational` : null,
    counts.unknown ? `${counts.unknown} not known` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The line under a site's badge: the failing checks, or `4/4 checks · 1 min ago`; otherwise the server's own words (why a state is unknown stays). */
function SiteLine({ summary }: { summary: ProjectHealthSummary }) {
  const site = siteOf(summary.primary);
  if (site.reason === 'checks_failing' || site.reason === 'checks_warning') {
    const names = site.failing_checks ?? [];
    return <>{names.length > 0 ? `Failing: ${names.slice(0, 2).join(', ')}${names.length > 2 ? ` and ${names.length - 2} more` : ''}` : site.headline}</>;
  }
  if (site.state === 'operational' && site.checks > 0 && site.last_checked_at) {
    return (
      <>
        <span className="visually-hidden">
          {site.passing} of {site.checks} checks passed, last checked {relativeTime(site.last_checked_at)}
        </span>
        <span aria-hidden="true">
          {site.passing}/{site.checks} checks · {relativeTime(site.last_checked_at)}
        </span>
      </>
    );
  }
  return <>{site.state === 'operational' ? (site.evidence ?? site.headline) : site.headline}</>;
}

const columns: Column<ProjectHealthSummary>[] = [
  {
    id: 'project',
    header: 'Project',
    card: 'title',
    width: 20,
    cell: (summary) => (
      <span className="cell-stack">
        <Link className="cell-stack__primary" to={`/app/projects/${summary.project_id}/overview`}>
          {summary.name}
        </Link>
        <span className="cell-stack__secondary">
          {[summary.primary?.environment_name, summary.platform === 'wordpress' ? 'WordPress' : summary.architecture === 'headless' ? 'Magento, headless' : 'Magento'].filter(Boolean).join(' · ')}
        </span>
      </span>
    ),
  },
  {
    id: 'site',
    header: 'Site',
    card: 'status',
    width: 24,
    cell: (summary) => {
      const site = siteOf(summary.primary);
      return (
        <span className="cell-stack">
          <SiteBadge state={site.state} stale={site.stale} />
          <span className="cell-stack__secondary"><SiteLine summary={summary} /></span>
        </span>
      );
    },
  },
  {
    id: 'deployment',
    header: 'Latest deployment',
    cardLabel: 'Latest deployment',
    width: 24,
    cell: (summary) => {
      const attempt = latestAttemptOf(summary.primary);
      if (!attempt) return <span className="muted">{summary.primary ? 'No deployments' : 'Not known'}</span>;
      const short = attemptNote(attempt);
      return (
        <span className="cell-stack">
          <span className="cell-inline">
            <OutcomeBadge outcome={attempt.outcome} />
            {short ? <span className="cell-stack__secondary">{short}</span> : null}
          </span>
          <span className="cell-stack__secondary">
            {attempt.deployment.component_name ? `${attempt.deployment.component_name} · ` : ''}
            <Timestamp iso={attempt.deployment.completed_at ?? attempt.deployment.created_at} />
          </span>
        </span>
      );
    },
  },
  {
    id: 'infrastructure',
    header: 'Infrastructure',
    width: 'fill',
    cell: (summary) => <InfrastructureSummary infra={infrastructureOf(summary.primary)} />,
  },
];

export function SitesOverview({ health, loading, error, onRetry }: { health: OrganizationHealth | null; loading: boolean; error: Error | null; onRetry?: () => void }) {
  if (loading && !health) return <LoadingRows label="Reading every project" rows={3} rowHeight="52px" />;
  if (error || !health) {
    // A failed read is never an all-clear.
    return (
      <p className="quiet-empty" data-testid="sites-error" role="status">
        Project state could not be read{error ? `: ${error.message}` : ''}{' '}
        {onRetry ? (
          <button type="button" className="button button--secondary button--small" onClick={onRetry}>
            Retry
          </button>
        ) : null}
      </p>
    );
  }
  const projects = [...health.projects].sort(bySiteUrgency);
  return (
    <ResponsiveTable
      rows={projects}
      columns={columns}
      rowKey={(summary) => summary.project_id}
      caption="Every project's live site, latest deployment and infrastructure"
      testId="sites-table"
      rowProps={(summary) => ({ testId: `site-row-${summary.slug || summary.project_id}` })}
      rowLink={(summary) => ({ to: `/app/projects/${summary.project_id}/overview`, accessibleName: `Open ${summary.name}` })}
    />
  );
}

/** Every project's attention items of the given categories, as feed rows. */
export function portfolioFeedItems(health: OrganizationHealth, categories: string[]): FeedItem[] {
  const rows: { item: OperationalAttentionItem; project: ProjectHealthSummary }[] = [];
  for (const project of health.projects) {
    for (const group of groupByCategory(project.attention)) {
      if (!categories.includes(group.category)) continue;
      for (const item of group.items) rows.push({ item, project });
    }
  }
  rows.sort((a, b) => {
    if (a.item.severity !== b.item.severity) return a.item.severity === 'critical' ? -1 : 1;
    return Date.parse(b.item.since ?? '') - Date.parse(a.item.since ?? '') || 0;
  });
  return rows.map(({ item, project }) => {
    const where = itemEnvironmentName(project, item);
    const to = portfolioItemPath(project, item);
    return {
      key: `${project.project_id}:${item.id}`,
      severity: item.severity,
      title: item.title,
      context: (
        <>
          <Link to={`/app/projects/${project.project_id}/overview`}>{project.name}</Link>
          {where ? ` · ${where}` : null}
        </>
      ),
      summary: item.summary || undefined,
      when: item.since ? { label: item.since_label || 'Since', at: item.since } : undefined,
      action: item.action && to ? { label: item.action.label, to } : undefined,
      related: (item.symptoms ?? []).map((symptom, index) => ({ key: `${item.id}-${index}`, title: symptom.title, detail: symptom.detail || undefined })),
    };
  });
}

/**
 * One titled part of the Dashboard's attention (CO-184, OR2). It shows the
 * items when there are any; "Could not read incidents" when the read failed
 * (not knowing never looks like nothing to report); and nothing at all when
 * the read was clean, because the absence of the section is the signal. The
 * one exception is `note`, a fact that is itself abnormal (servers without
 * contact), which keeps the section visible.
 */
export function PortfolioFeedSection({
  health,
  loading = false,
  error = null,
  title,
  note,
  noun,
  categories,
  testId,
}: {
  health: OrganizationHealth | null;
  /** The organization's health is still being read. */
  loading?: boolean;
  /** Why there is no health to show. */
  error?: Error | null;
  title: string;
  /** An abnormal fact that keeps the section visible, such as servers without contact. */
  note?: string | null;
  /** What the section lists, plural, for its loading and error lines ("incidents"). */
  noun: string;
  categories: string[];
  testId: string;
}) {
  const headingId = `${testId}-title`;
  const items = health ? portfolioFeedItems(health, categories) : [];
  // A clean read with nothing to say is not drawn at all.
  if (health && items.length === 0 && !note) return null;
  return (
    <section className="dash-section" aria-labelledby={headingId} data-testid={testId}>
      <div className="dash-section__head">
        <h2 id={headingId} className="dash-section__title">
          {title}
          {items.length > 0 ? <span className="dash-section__count">{items.length}</span> : null}
        </h2>
        {note ? <p className="dash-section__desc">{note}</p> : null}
      </div>
      {!health && loading ? <LoadingRows label={`Reading ${noun}`} rows={1} rowHeight="40px" /> : null}
      {!health && !loading ? (
        <p className="quiet-empty dash-section__unread" role="status" data-testid={`${testId}-unread`}>
          Could not read {noun}
          {error?.message ? `: ${error.message}` : ''}
        </p>
      ) : null}
      {items.length > 0 ? <AttentionFeed items={items} label={title} limit={5} /> : null}
    </section>
  );
}

/** "2 servers without contact" for the infrastructure heading; null when every server is in contact. */
export function serverContactLine(health: OrganizationHealth | null): string | null {
  const servers = health?.servers;
  if (!servers || servers.length === 0) return null;
  const silent = servers.filter((server) => server.agent.state === 'stale' || server.agent.state === 'offline').length;
  return silent > 0 ? `${plural(silent, 'server')} without contact` : null;
}
